import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Fingerprint, PhoneCall } from 'lucide-react';
import { useGetCheckOptionsQuery } from '@/store/api/apiSlice';
import { FALLBACK_CHECK_OPTIONS, visibleCheckOptions } from '@/utils/checkOptions';
import type { CheckOption } from '@/types';
import PlanRail from './PlanRail';
import CallbackForm from './CallbackForm';
import Sheet from './Sheet';

/**
 * The /intelligence one-pager.
 *
 * Everything a prospect needs — the positioning, the price, and the way to reach
 * us — resolved in a single view with no onward navigation. Two columns from
 * `lg` up; below that it stacks, with the plans becoming a horizontal snap rail
 * so the callback form stays close to the headline.
 */
export default function Intelligence() {
    const { data, isLoading } = useGetCheckOptionsQuery();
    const [selectedPlan, setSelectedPlan] = useState<CheckOption | null>(null);
    const [callbackOpen, setCallbackOpen] = useState(false);

    const plans = useMemo(() => {
        const source = data && data.length > 0 ? data : FALLBACK_CHECK_OPTIONS;
        return visibleCheckOptions(source).slice(0, 3);
    }, [data]);

    // Both plan CTAs — the card's "Talk to us about X" and the details sheet's
    // "Request a call about X" — route through here, so both open the same modal
    // with the plan attached rather than scrolling the page to the inline form.
    const choosePlan = useCallback((plan: CheckOption) => {
        setSelectedPlan(plan);
        setCallbackOpen(true);
    }, []);

    const closeCallback = useCallback(() => setCallbackOpen(false), []);

    return (
        <main className="relative flex min-h-dvh flex-col overflow-x-hidden bg-[#F7F6F3] text-[#15171C]">
            {/* Faint blueprint grid + a warm bloom behind the headline: enough texture
                to read as an intelligence product without competing with the copy. */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 opacity-[0.5]"
                style={{
                    backgroundImage:
                        'linear-gradient(to right, rgba(21,23,28,0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(21,23,28,0.06) 1px, transparent 1px)',
                    backgroundSize: '64px 64px',
                    maskImage: 'radial-gradient(ellipse 90% 65% at 25% 0%, black 25%, transparent 75%)',
                    WebkitMaskImage: 'radial-gradient(ellipse 90% 65% at 25% 0%, black 25%, transparent 75%)',
                }}
            />
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-32 -left-24 h-[420px] w-[420px] rounded-full bg-[#FF6B35]/12 blur-[120px] xl:h-[560px] xl:w-[560px]"
            />

            <div className="relative mx-auto flex w-full max-w-[1500px] flex-1 flex-col px-4 py-4 sm:px-6 lg:px-10 lg:py-6 2xl:max-w-[1700px] 2xl:px-16">
                {/* Brand strip */}
                <header className="flex shrink-0 items-center justify-between gap-3 pb-6 lg:pb-8">
                    <Link to="/" className="flex items-center gap-2">
                        <Fingerprint className="h-5 w-5 text-[#FF6B35] xl:h-6 xl:w-6" aria-hidden="true" />
                        <span className="text-base font-bold tracking-tight xl:text-lg">
                            your<span className="text-[#FF6B35]">checka</span>
                        </span>
                    </Link>
                    <button
                        type="button"
                        onClick={() => setCallbackOpen(true)}
                        className="inline-flex items-center gap-2 rounded-full bg-[#15171C] px-4 py-2 text-[12.5px] font-semibold text-white transition-colors hover:bg-[#FF6B35] sm:px-5 sm:py-2.5 sm:text-[13.5px] xl:text-[14.5px]"
                    >
                        <PhoneCall className="h-3.5 w-3.5 xl:h-4 xl:w-4" aria-hidden="true" />
                        Request a callback
                    </button>
                </header>

                {/* The two-column split needs ~1440px before it stops squeezing the
                    three plan cards below a readable width — 5/12 for the headline
                    plus 3×230px of cards plus gutters. Under that it stacks, which
                    gives the cards the full page width instead of half of it. */}
                {/* `content-start` matters: the grid is a flex child with `flex-1`, and
                    grid's default `align-content: stretch` spreads the leftover height
                    between the two auto rows — which is what opened the gap under the
                    headline. Top-align when stacked, centre only in the two-column layout. */}
                <div className="grid flex-1 grid-cols-1 content-start gap-6 min-[1440px]:grid-cols-12 min-[1440px]:content-center min-[1440px]:items-center min-[1440px]:gap-14 2xl:gap-20">
                    {/* ---------- Left: the positioning ---------- */}
                    <section className="min-[1440px]:col-span-5">
                        <p className="mb-4 flex items-center gap-2.5 font-mono text-[10px] tracking-[0.2em] text-[#FF6B35] uppercase xl:text-[12px]">
                            <span className="h-px w-7 bg-[#FF6B35] xl:w-10" aria-hidden="true" />
                            Why we're different
                        </p>

                        <h1 className="max-w-[18ch] text-[32px] leading-[1.08] font-bold tracking-[-0.02em] text-balance sm:max-w-[22ch] sm:text-[44px] md:text-[52px] lg:text-[58px] min-[1440px]:max-w-none min-[1440px]:text-[54px] 2xl:text-[64px]">
                            We don't just run background checks,{' '}
                            <span className="text-[#FF6B35]">we build intelligence on new hires.</span>
                        </h1>

                        {/* Only in the two-column layout, where the headline column has
                            height to fill. Stacked, it just pushes the plans down. */}
                        <p className="mt-6 hidden max-w-lg text-[17px] leading-relaxed text-[#6B6862] min-[1440px]:block 2xl:text-[19px]">
                            Two decades of intelligence gathering, applied to the person you're about to trust with your
                            business, your home, and your family.
                        </p>
                    </section>

                    {/* ---------- Right: the price ---------- */}
                    {/* The form lives only in the modal — every CTA here opens it,
                        so an inline copy would just be a second thing to keep in
                        sync and a second set of fields for autofill to fight over. */}
                    <div className="flex min-w-0 flex-col min-[1440px]:col-span-7">
                        <PlanRail plans={plans} isLoading={isLoading} onChoosePlan={choosePlan} />
                    </div>
                </div>

                {/* Deliberately small: a physical address is a trust signal on a page
                    asking for a phone number, but it shouldn't compete with the price. */}
                <footer className="mt-10 shrink-0 border-t border-[#E4E1DA] pt-5">
                    <h2 className="font-mono text-[10px] tracking-[0.2em] text-[#8A857C] uppercase xl:text-[11px]">
                        Company information
                    </h2>
                    <div className="mt-3 flex flex-col gap-x-10 gap-y-2 text-[13px] leading-snug text-[#6B6862] sm:flex-row sm:flex-wrap sm:items-center xl:text-sm">
                        <address className="flex flex-col gap-1 not-italic sm:flex-row sm:gap-x-8">
                            <span>42 Montgomery Road, Yaba, Lagos.</span>
                            <span>1 Aba Close, Area 8, Garki, Abuja.</span>
                        </address>
                        <span className="flex flex-wrap items-center gap-x-4 gap-y-1 sm:ml-auto">
                            <a href="tel:+2348064277731" className="transition-colors hover:text-[#FF6B35]">
                                08064277731
                            </a>
                            <a href="mailto:hi@yourchecka.com" className="transition-colors hover:text-[#FF6B35]">
                                hi@yourchecka.com
                            </a>
                        </span>
                    </div>
                </footer>
            </div>

            {/* One modal for every route in: the header CTA, both plan CTAs, and the
                details sheet. */}
            <Sheet open={callbackOpen} onClose={closeCallback} label="Request a callback">
                <h2 className="pr-8 text-lg font-semibold text-[#15171C] xl:text-xl">Request a callback</h2>
                {/* The attached plan is shown rather than sent silently — it rides
                    along to the backend, so the visitor should be able to see it. */}
                {selectedPlan && (
                    <p className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-[#FF6B35]/30 bg-[#FFF3EE] px-2.5 py-1 font-mono text-[10.5px] tracking-wide text-[#C2410C] uppercase">
                        re: {selectedPlan.name}
                    </p>
                )}
                <p className="mt-2 mb-4 text-[13.5px] leading-snug text-[#6B6862]">
                    {selectedPlan
                        ? `Leave your details and we'll call you within 24hrs.`
                        : `Leave your details and we'll call you within 24hrs.`}
                </p>
                <CallbackForm selectedPlanId={selectedPlan?.id} />
            </Sheet>
        </main>
    );
}
